import { FC, useState } from 'react';
import { Icon } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import LanguageSelectorPopover, {
  useLanguageLabel,
} from './LanguageSelectorPopover';

export type TranslationSettings = {
  sourceLanguage?: string;
  translate: {
    postText?: string;
    firstComment?: string;
    imageText?: string;
  } | null;
};

type Props = {
  pageId: number;
  value: TranslationSettings;
  onChange: (settings: TranslationSettings) => void;
  disabled?: boolean;
  selectedBrandProfileId?: number | null;
  onBrandProfileChange?: (brandProfileId: number | null) => void;
};

const TranslationLanguageDropdown: FC<Props> = ({
  pageId,
  value,
  onChange,
  disabled,
  selectedBrandProfileId,
  onBrandProfileChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentTargetLanguage =
    value.translate?.postText ||
    value.translate?.firstComment ||
    value.translate?.imageText ||
    null;

  const selectedLanguageLabel = useLanguageLabel(currentTargetLanguage);

  const handleLanguageSelect = (languageValue: string | null) => {
    if (languageValue) {
      onChange({
        ...value,
        translate: {
          postText: languageValue,
          firstComment: languageValue,
          imageText: languageValue,
        },
      });
    } else {
      onChange({
        ...value,
        translate: null,
      });
    }
  };

  return (
    <LanguageSelectorPopover
      id={`dropdown-${pageId}`}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      selectedLanguage={currentTargetLanguage}
      onLanguageSelect={handleLanguageSelect}
      showOriginalOption={true}
      disabled={disabled}
      showBrandProfileSelector={!!onBrandProfileChange}
      selectedBrandProfileId={selectedBrandProfileId}
      onBrandProfileChange={onBrandProfileChange}
      infoText={t('translation.chooseLanguageInfo', {
        defaultValue:
          'Choose a language to translate image text, post text and first comment if available.',
      })}
      trigger={
        <button
          type="button"
          className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-grayscale-100 hover:tw-text-grayscale-180 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          data-cy={`translation-dropdown-trigger-${pageId}`}
        >
          <span>{selectedLanguageLabel}</span>
          <Icon icon={faChevronDown} size="sm" />
        </button>
      }
    />
  );
};

export default TranslationLanguageDropdown;
